import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserRole,
  type StoreSolutionDto,
  type SolutionCategory,
  type SolutionStatus,
} from '@bhops/shared';
import { solutionsApi } from '@/api/solutions';
import { extractApiError } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { kstYmd } from '@/lib/format-date';
import { Button, Field, FormError, Select, Textarea } from '@/components/Form';
import { Modal } from '@/components/Modal';
import { useToast } from '@/components/Toast';

// S2 §9.3 (Phase 2.8) — 매장 상세 "솔루션" 섹션.
//
// 솔루션(불판/Store ID/조리 프로파일)은 로봇 자체와 무관하고 매장에 귀속.
// 기존 로봇 상세 솔루션 탭(RobotSolutionsTab)의 UX를 매장 단위로 이동:
//   상단: 활성 솔루션 카드 (deactivatedAt IS NULL) + 30일 KPI.
//   하단: 비활성(해제) 이력 리스트.

const CATEGORY_LABEL: Record<SolutionCategory, string> = {
  MEAT: '육류',
  SEAFOOD: '해산물',
  VEGETABLE: '채소',
  DEMO: '데모',
  CUSTOM: '맞춤',
};

const STATUS_LABEL: Record<SolutionStatus, string> = {
  BETA: '시범',
  STABLE: '정식',
  DEPRECATED: '단종 예정',
  RETIRED: '단종',
};

const STATUS_COLOR: Record<SolutionStatus, string> = {
  BETA: 'var(--fg-brand)',
  STABLE: 'var(--robot-operating)',
  DEPRECATED: 'var(--dday-warning)',
  RETIRED: 'var(--fg-muted)',
};

export function StoreSolutionsSection({ storeId }: { storeId: string }) {
  const role = useAuthStore((s) => s.user?.role);
  const canEdit =
    role === UserRole.ADMIN || role === UserRole.MANAGER || role === UserRole.FIELD_MANAGER;
  const [attachOpen, setAttachOpen] = useState(false);
  const [deactivating, setDeactivating] = useState<StoreSolutionDto | null>(null);

  const q = useQuery({
    queryKey: ['store-solutions', storeId],
    queryFn: () => solutionsApi.listForStore(storeId),
    staleTime: 30_000,
  });

  const { active, inactive } = useMemo(() => {
    const items = q.data ?? [];
    return {
      active: items.filter((s) => !s.deactivatedAt),
      inactive: items.filter((s) => s.deactivatedAt),
    };
  }, [q.data]);

  return (
    <section>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginBottom: 8,
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <h3 className="kuun-h3" style={{ margin: 0 }}>
            솔루션
          </h3>
          <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
            활성 {active.length}건
          </span>
        </div>
        {canEdit && (
          <Button
            type="button"
            onClick={() => setAttachOpen(true)}
            data-testid="store-solution-attach-button"
          >
            + 솔루션 추가
          </Button>
        )}
      </div>

      {q.isLoading ? (
        <div style={card}>
          <div className="kuun-caption">불러오는 중…</div>
        </div>
      ) : q.error ? (
        <div style={{ ...card, color: 'var(--fg-danger)' }}>{extractApiError(q.error)}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {active.length === 0 ? (
            <div style={emptyBlock}>
              {canEdit
                ? '도입된 솔루션이 없습니다. [+ 솔루션 추가] 버튼으로 등록해 주세요.'
                : '도입된 솔루션이 없습니다.'}
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: 12,
              }}
            >
              {active.map((s) => (
                <ActiveSolutionCard
                  key={s.id}
                  solution={s}
                  canEdit={canEdit}
                  onDeactivate={() => setDeactivating(s)}
                />
              ))}
            </div>
          )}

          {inactive.length > 0 && (
            <div>
              <h4
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--fg-muted)',
                  margin: '0 0 6px',
                }}
              >
                비활성 / 단종 이력 ({inactive.length})
              </h4>
              <div style={{ ...card, padding: 0 }}>
                <table style={tbl}>
                  <thead>
                    <tr>
                      <th style={th}>솔루션</th>
                      <th style={th}>활성 기간</th>
                      <th style={th}>해제 사유</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inactive.map((s) => (
                      <tr key={s.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                        <td style={td}>
                          <div style={{ fontWeight: 600 }}>{s.solutionDef.displayName}</div>
                          <div
                            style={{
                              fontSize: 11,
                              fontFamily: 'var(--font-mono)',
                              color: 'var(--fg-muted)',
                            }}
                          >
                            {s.solutionDef.code}
                          </div>
                        </td>
                        <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                          {/* 한국시간 통일 5차(26-08-14) — 활성/해제 시각은 **진짜 순간**.
                              이 화면(솔루션 붙이기/해제)은 날짜를 보내지 않으므로 서버가
                              `dto.activatedAt ? … : new Date()` 의 뒷가지로 지금을 찍는다. */}
                          {kstYmd(s.activatedAt) ?? '-'} ~ {kstYmd(s.deactivatedAt) ?? '-'}
                        </td>
                        <td style={td}>{s.deactivationReason ?? '–'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      <StoreSolutionAttachModal
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        storeId={storeId}
        alreadyActiveIds={active.map((s) => s.solutionDefId)}
      />
      <StoreSolutionDeactivateModal
        open={deactivating !== null}
        onClose={() => setDeactivating(null)}
        solution={deactivating}
        storeId={storeId}
      />
    </section>
  );
}

function ActiveSolutionCard({
  solution: s,
  canEdit,
  onDeactivate,
}: {
  solution: StoreSolutionDto;
  canEdit: boolean;
  onDeactivate: () => void;
}) {
  const statusColor = STATUS_COLOR[s.solutionDef.status];
  return (
    <div
      style={{
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--r-card)',
        background: 'var(--bg-surface)',
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{s.solutionDef.displayName}</div>
          <div
            style={{
              fontSize: 11,
              fontFamily: 'var(--font-mono)',
              color: 'var(--fg-muted)',
              marginTop: 2,
            }}
          >
            {s.solutionDef.code}
          </div>
        </div>
        <span
          style={{
            fontSize: 10,
            padding: '2px 6px',
            borderRadius: 'var(--r-pill)',
            border: `1px solid ${statusColor}`,
            color: statusColor,
            whiteSpace: 'nowrap',
          }}
        >
          {STATUS_LABEL[s.solutionDef.status]}
        </span>
      </div>

      <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
        {CATEGORY_LABEL[s.solutionDef.category]} · 활성 {kstYmd(s.activatedAt) ?? '-'}
      </div>

      {/* 30일 KPI strip (lastMeasuredAt 가 있을 때만). */}
      {s.lastMeasuredAt ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 8,
            paddingTop: 8,
            borderTop: '1px dashed var(--border-subtle)',
          }}
        >
          <KpiCell label="가동률" value={s.uptimePercent != null ? `${s.uptimePercent}%` : '–'} />
          <KpiCell label="실행" value={s.totalRuns?.toLocaleString() ?? '–'} />
          <KpiCell
            label="실패"
            value={
              s.failureCount != null && s.totalRuns
                ? `${s.failureCount} (${Math.round((s.failureCount / s.totalRuns) * 100)}%)`
                : '–'
            }
          />
        </div>
      ) : (
        <div
          style={{
            fontSize: 11,
            color: 'var(--fg-subtle)',
            paddingTop: 8,
            borderTop: '1px dashed var(--border-subtle)',
            fontStyle: 'italic',
          }}
        >
          KPI 측정 대기 중입니다.
        </div>
      )}

      {canEdit && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            paddingTop: 6,
            borderTop: '1px dashed var(--border-subtle)',
            marginTop: 4,
          }}
        >
          <button
            type="button"
            onClick={onDeactivate}
            data-testid={`store-solution-deactivate-${s.id}`}
            style={{
              padding: '4px 10px',
              fontSize: 11,
              border: '1px solid var(--fg-danger)',
              background: 'transparent',
              color: 'var(--fg-danger)',
              borderRadius: 'var(--r-btn)',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            해제
          </button>
        </div>
      )}
    </div>
  );
}

function KpiCell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: 'var(--fg-muted)' }}>{label}</div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
          fontFamily: 'var(--font-mono)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
    </div>
  );
}

const ATTACH_STATUS_LABEL: Record<string, string> = {
  BETA: '시범',
  STABLE: '정식',
  DEPRECATED: '단종 예정',
};

function StoreSolutionAttachModal({
  open,
  onClose,
  storeId,
  alreadyActiveIds,
}: {
  open: boolean;
  onClose: () => void;
  storeId: string;
  /** 이미 활성으로 도입된 SolutionDef.id 리스트 — 중복 선택 차단. */
  alreadyActiveIds: string[];
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [solutionDefId, setSolutionDefId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const defsQ = useQuery({
    queryKey: ['solutions', { active: true }],
    queryFn: () => solutionsApi.list(),
    enabled: open,
    staleTime: 60_000,
  });

  // RETIRED 와 이미 도입된 것은 제외.
  const available = (defsQ.data ?? []).filter(
    (d) => d.status !== 'RETIRED' && !alreadyActiveIds.includes(d.id),
  );

  const attachMx = useMutation({
    mutationFn: () => solutionsApi.attachToStore(storeId, { solutionDefId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['store-solutions', storeId] });
      qc.invalidateQueries({ queryKey: ['solutions'] });
      toast.success('솔루션을 추가했습니다.');
      handleClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function handleClose(): void {
    if (attachMx.isPending) return;
    setSolutionDefId('');
    setError(null);
    onClose();
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setError(null);
    if (!solutionDefId) return setError('솔루션을 선택해 주세요.');
    attachMx.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="솔루션 추가"
      width={480}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={attachMx.isPending}
          >
            취소
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={attachMx.isPending || !solutionDefId}
            data-testid="store-solution-attach-submit"
          >
            {attachMx.isPending ? '추가 중…' : '추가'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {error && <FormError>{error}</FormError>}

        <Field
          label="솔루션"
          htmlFor="store-solution-def"
          required
          hint={
            available.length === 0 && !defsQ.isLoading
              ? '추가할 수 있는 솔루션이 없습니다. 모두 도입했거나 단종된 솔루션입니다.'
              : '단종 예정 솔루션도 도입할 수 있지만, 새로 도입하는 것은 되도록 피해 주세요.'
          }
        >
          <Select
            id="store-solution-def"
            value={solutionDefId}
            onChange={(e) => setSolutionDefId(e.target.value)}
            disabled={defsQ.isLoading || available.length === 0}
            data-testid="store-solution-def-select"
          >
            <option value="">— 선택 —</option>
            {available.map((d) => (
              <option key={d.id} value={d.id}>
                [{ATTACH_STATUS_LABEL[d.status] ?? d.status}] {d.displayName} ({d.code})
              </option>
            ))}
          </Select>
        </Field>

        <div
          style={{
            marginTop: 8,
            padding: 10,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--r-card)',
            fontSize: 12,
            color: 'var(--fg-muted)',
          }}
        >
          솔루션은 불판, Store ID, 조리 프로파일처럼 매장 단위로 도입됩니다. 같은 솔루션을 두 번
          활성화할 수는 없습니다.
        </div>
      </form>
    </Modal>
  );
}

const REASON_OPTIONS = [
  { value: 'CUSTOMER_REQUEST', label: '고객 요청' },
  { value: 'INCOMPATIBLE', label: '호환 불가 (펌웨어 / 부품)' },
  { value: 'UPGRADE', label: '업그레이드 (신 버전 도입 예정)' },
  { value: 'PERFORMANCE', label: '성능 미달' },
  { value: 'OTHER', label: '기타' },
];

function StoreSolutionDeactivateModal({
  open,
  onClose,
  solution,
  storeId,
}: {
  open: boolean;
  onClose: () => void;
  solution: StoreSolutionDto | null;
  storeId: string;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [reasonKey, setReasonKey] = useState('CUSTOMER_REQUEST');
  const [extra, setExtra] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReasonKey('CUSTOMER_REQUEST');
      setExtra('');
      setError(null);
    }
  }, [open]);

  const deactivateMx = useMutation({
    mutationFn: () => {
      if (!solution) throw new Error('No solution');
      const reasonLabel = REASON_OPTIONS.find((o) => o.value === reasonKey)?.label ?? reasonKey;
      const fullReason = extra.trim() ? `${reasonLabel} — ${extra.trim()}` : reasonLabel;
      return solutionsApi.deactivate(solution.id, { deactivationReason: fullReason });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['store-solutions', storeId] });
      qc.invalidateQueries({ queryKey: ['solutions'] });
      toast.success('솔루션을 해제했습니다.');
      handleClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function handleClose(): void {
    if (deactivateMx.isPending) return;
    onClose();
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setError(null);
    if (!solution) return;
    deactivateMx.mutate();
  }

  if (!solution) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="솔루션 해제"
      width={480}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={deactivateMx.isPending}
          >
            취소
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={handleSubmit}
            disabled={deactivateMx.isPending}
            data-testid="store-solution-deactivate-submit"
          >
            {deactivateMx.isPending ? '해제 중…' : '해제'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {error && <FormError>{error}</FormError>}

        <div
          style={{
            padding: 10,
            background: 'var(--bg-sunken)',
            borderRadius: 'var(--r-card)',
            marginBottom: 4,
            fontSize: 13,
          }}
        >
          <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>대상</div>
          <div style={{ fontWeight: 600, marginTop: 2 }}>{solution.solutionDef.displayName}</div>
          <div
            style={{
              fontSize: 11,
              color: 'var(--fg-muted)',
              fontFamily: 'var(--font-mono)',
              marginTop: 2,
            }}
          >
            {solution.solutionDef.code}
          </div>
        </div>

        <Field label="해제 사유" htmlFor="store-solution-reason" required>
          <Select
            id="store-solution-reason"
            value={reasonKey}
            onChange={(e) => setReasonKey(e.target.value)}
            data-testid="store-solution-deactivation-reason"
          >
            {REASON_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="비고 (선택)" htmlFor="store-solution-extra" hint="상세 사유나 다음 조치 등">
          <Textarea
            id="store-solution-extra"
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            placeholder="예: 신 버전 v2.5 도입 예정"
            rows={3}
          />
        </Field>
      </form>
    </Modal>
  );
}

const card: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
  padding: 16,
  overflow: 'auto',
};
const emptyBlock: CSSProperties = {
  padding: 24,
  textAlign: 'center',
  color: 'var(--fg-muted)',
  border: '1px dashed var(--border-default)',
  borderRadius: 'var(--r-card)',
  fontSize: 13,
};
const tbl: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th: CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  borderBottom: '1px solid var(--border-subtle)',
};
const td: CSSProperties = { padding: '8px 10px', verticalAlign: 'middle' };
